import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { logger } from '../../datadog';
import { useGetTelehealthSessionInfo } from '../../hooks';
import { useEnterWaitingRoomCallback } from '../nav/useEnterWaitingRoomCallback';
import { didManuallyResolveSoftBlockAtom, isSoftBlockResolvedAtom, visitorNameAtom } from '../state';
import { Page } from './Page';
import { PreSessionMeasures } from './PreSessionMeasures';
import { SoftBlockAutoTransitionToast } from './SoftBlockAutoTransitionToast';
import { SoftBlockJoiningTransition } from './SoftBlockJoiningTransition';
import { useSoftBlockAutoTransition } from './useSoftBlockAutoTransition';
import { useSoftBlockFlowTracking } from './useSoftBlockTracking';

export function PageWrapper() {
  const [isFillingMeasures, setIsFillingMeasures] = useState(false);
  const [isJoining, setIsJoining] = useState(false);

  const enterWaitingRoom = useEnterWaitingRoomCallback();
  const setIsSoftBlockResolved = useSetAtom(isSoftBlockResolvedAtom);
  const setDidManuallyResolveSoftBlock = useSetAtom(didManuallyResolveSoftBlockAtom);
  const visitorName = useAtomValue(visitorNameAtom);
  const { trackGetStartedClicked, trackGuardrailPromptShown, trackAutoTransitioned } = useSoftBlockFlowTracking();

  const { providerShortId, patientShortId } = useParams();
  const { data } = useGetTelehealthSessionInfo(providerShortId, patientShortId);
  const providerName = data?.telehealthSessionInfo.provider.name ?? 'your provider';

  const goToWaitingRoom = useCallback(() => {
    // Resolving the soft block transitions to the waiting room (via
    // `enterWaitingRoom`) and prevents re-entering the soft block. Manual exits
    // (skip / completion) also flag `didManuallyResolveSoftBlock` so the
    // waiting-room measures drawer auto-open stays suppressed — the client was
    // already given the chance to complete measures here.
    setIsSoftBlockResolved(true);
    setDidManuallyResolveSoftBlock(true);
    enterWaitingRoom(visitorName);
  }, [enterWaitingRoom, setIsSoftBlockResolved, setDidManuallyResolveSoftBlock, visitorName]);

  // Completing the final measure (Submit & join) shows a brief "Joining session
  // now..." interstitial before entering the waiting room. The soft block is
  // already resolved on submit (see `PreSessionMeasuresActions`), so this is
  // purely a transitional loading screen.
  const handleMeasuresComplete = useCallback(() => {
    logger.info('Soft-block resolved', {
      feature: 'soft_block',
      outcome: 'completed',
      patientShortId,
      providerShortId,
    });
    setIsJoining(true);
  }, [patientShortId, providerShortId]);

  const handleSkip = useCallback(() => {
    logger.info('Soft-block resolved', {
      feature: 'soft_block',
      outcome: 'skipped',
      patientShortId,
      providerShortId,
    });
    goToWaitingRoom();
  }, [goToWaitingRoom, patientShortId, providerShortId]);

  const autoTransitionToWaitingRoom = useCallback(() => {
    // Auto-transition (ran out of time) resolves the soft block so the client
    // exits to the waiting room, but intentionally does NOT flag manual
    // resolution: they never got to act on their measures, so the waiting-room
    // drawer should still auto-open to prompt them.
    logger.info('Soft-block resolved', {
      feature: 'soft_block',
      outcome: 'auto_transitioned',
      patientShortId,
      providerShortId,
    });
    trackAutoTransitioned();
    setIsSoftBlockResolved(true);
    enterWaitingRoom(visitorName);
  }, [trackAutoTransitioned, enterWaitingRoom, setIsSoftBlockResolved, visitorName, patientShortId, providerShortId]);

  // Guardrail: warn then auto-transition the client to the waiting room if the
  // soft-block flow starts cutting into their appointment start time. Applies
  // across every page of the flow.
  const { secondsToAutoTransition, autoTransitionToastIsVisible } = useSoftBlockAutoTransition({
    data,
    onAutoTransition: autoTransitionToWaitingRoom,
    onPromptShown: trackGuardrailPromptShown,
  });

  const handlePrimaryAction = useCallback(() => {
    trackGetStartedClicked();
    setIsFillingMeasures(true);
  }, [trackGetStartedClicked]);

  return (
    <>
      {autoTransitionToastIsVisible && (
        <SoftBlockAutoTransitionToast secondsToAutoTransition={secondsToAutoTransition} />
      )}
      {isJoining ? (
        <SoftBlockJoiningTransition onTransitionComplete={goToWaitingRoom} />
      ) : isFillingMeasures ? (
        <PreSessionMeasures onComplete={handleMeasuresComplete} onSkip={handleSkip} />
      ) : (
        <Page providerName={providerName} onPrimaryAction={handlePrimaryAction} />
      )}
    </>
  );
}
